import { api } from './client';
import type { ShirtColor, ShirtCut, Size } from '../types';

export type ProductSize = {
  id: number;
  cut: ShirtCut;
  size: Size;
  display_order: number;
};

export type ProductImage = {
  id: number;
  url: string;
  display_order: number;
};

export type Product = {
  id: string;
  color: ShirtColor;
  name: string;
  description: string;
  price_grosze: number;
  material: string;
  printing: string;
  images: ProductImage[];
  /** First entry of `images`, or null. Prefer `images`. */
  image_url: string | null;
  display_order: number;
  sizes: ProductSize[];
};

export const listProducts = async (): Promise<{ products: Product[] }> => {
  const raw = await api.get<{ products: RawProduct[] }>('/products');
  return { products: (raw?.products ?? []).map(normalizeProduct) };
};

type RawProduct = Partial<Product> & { images?: unknown; image_url?: string | null };

// The catalogue is served `s-maxage=60, stale-while-revalidate=300`, so for a few minutes after
// a release a browser can run the new bundle against a response cached before it. A gallery-less
// response used to reach the components as `images: undefined` and white-screen the shop, so the
// wire shape is narrowed here rather than trusted: the Product type describes what we send, not
// what a cache happens to hold. An older response still carries image_url, which is the same
// photo the gallery would have shown as slot 0.
function normalizeProduct(raw: RawProduct): Product {
  const images = Array.isArray(raw.images)
    ? (raw.images as ProductImage[]).filter((i) => i && typeof i.url === 'string')
    : raw.image_url
      ? [{ id: -1, url: raw.image_url, display_order: 0 }]
      : [];

  return {
    ...(raw as Product),
    images,
    image_url: raw.image_url ?? images[0]?.url ?? null,
    sizes: Array.isArray(raw.sizes) ? raw.sizes : [],
  };
}
